/**
 * Home Page - Landing Page
 * Public homepage with hero, courses preview, features, and more
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navbar, Footer, CourseCard } from '../../components';
import { formatCourseThumbnail } from '../../utils/imageUtils';
import api from '../../services/api';
import SEO from '../../components/common/SEO';

// Skeleton loader for course cards
const CourseCardSkeleton = () => (
  <div className="lh-course-card card overflow-hidden animate-pulse">
    <div className="lh-course-card__thumbnail relative overflow-hidden bg-dark-200 aspect-[16/10]" />
    <div className="lh-course-card__content p-5">
      <div className="h-6 bg-dark-200 rounded mb-2" />
      <div className="h-4 bg-dark-200 rounded w-2/3 mb-3" />
      <div className="flex gap-4 mb-3">
        <div className="h-4 bg-dark-200 rounded w-16" />
        <div className="h-4 bg-dark-200 rounded w-20" />
      </div>
      <div className="h-4 bg-dark-200 rounded w-24 mb-4" />
      <div className="flex justify-between items-center">
        <div className="h-6 bg-dark-200 rounded w-20" />
        <div className="h-4 bg-dark-200 rounded w-24" />
      </div>
    </div>
  </div>
);

const howItWorks = [
  {
    step: 1,
    icon: (
      <svg className="lh-home__how-icon w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    title: 'Sign Up with Google',
    description: 'Create your account in seconds using Google authentication.',
  },
  {
    step: 2,
    icon: (
      <svg className="lh-home__how-icon w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
    title: 'Choose a Course',
    description: 'Browse our extensive library of courses and pick what interests you.',
  },
  {
    step: 3,
    icon: (
      <svg className="lh-home__how-icon w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    title: 'Learn at Your Pace',
    description: 'Study on your schedule with lifetime access to all course materials.',
  },
  {
    step: 4,
    icon: (
      <svg className="lh-home__how-icon w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: 'Get Certified',
    description: 'Earn recognized certificates upon successful course completion.',
  },
];

const whyChooseUs = [
  {
    icon: (
      <svg className="lh-home__why-icon w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
    ),
    title: 'Expert Instructors',
    description: 'Learn from industry professionals with years of real-world experience.',
  },
  {
    icon: (
      <svg className="lh-home__why-icon w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
    title: 'Live & Recorded',
    description: 'Access live classes and watch recordings at your convenience.',
  },
  {
    icon: (
      <svg className="lh-home__why-icon w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: 'Affordable Pricing',
    description: 'Quality education at prices that won\'t break the bank.',
  },
];

const Home = () => {
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const fetchFeaturedCourses = async () => {
      try {
        const data = await api.getCourses({ limit: '6', sort: 'popular' });
        const formattedCourses = (data.courses || []).map(course => {
          const mins = course.liveClassDurationMins || 0;
          const h = Math.floor(mins / 60);
          const m = mins % 60;
          let duration = '';
          if (h > 0 && m > 0) duration = `${h}h ${m}m`;
          else if (h > 0) duration = `${h}h`;
          else if (m > 0) duration = `${m}m`;

          return {
            id: course._id,
            _id: course._id,
            title: course.title,
            thumbnail: formatCourseThumbnail(course.thumbnail),
            duration: duration || null,
            liveClassCount: course.liveClassCount || 0,
            level: course.level,
            category: course.category,
          };
        });
        setFeaturedCourses(formattedCourses);
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setLoadingCourses(false);
      }
    };

    fetchFeaturedCourses();
  }, []);

  return (
    <div className="lh-home-page min-h-screen bg-white">
      <SEO
        canonicalPath="/"
        ogImage="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1200"
        keywords={['online learning', 'tech courses', 'career growth', 'expert instructors', 'live online classes', 'learn coding online', 'IT training']}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'EducationalOrganization',
          name: 'Lotus Arc Technologies',
          url: window.location.origin,
          logo: `${window.location.origin}/logo.svg`,
          description: 'Master future-ready skills with expert-led online courses in React, Java, Python, SQL, Machine Learning, and AI.',
          sameAs: [],
        }}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="lh-home__hero pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-br from-dark-50 via-white to-primary-50">
        <div className="lh-home__hero-container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lh-home__hero-content grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="lh-home__hero-text animate-fade-in">
              <span className="lh-home__hero-badge inline-block bg-primary-100 text-primary-700 text-sm font-medium px-4 py-1.5 rounded-full mb-6">
                🚀 Start Learning Today
              </span>
              <h1 className="lh-home__hero-title text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-dark-900 leading-tight mb-4 sm:mb-6">
                Learn <span className="lh-home__hero-title-gradient text-gradient">Future-Ready</span> Skills Online
              </h1>
              <div className="lh-home__hero-subtitle-wrapper mb-8 max-w-3xl">
                <p className="lh-home__hero-subtitle text-base sm:text-lg md:text-xl text-dark-500 leading-relaxed">
                  At Lotus Arc Technologies, we are committed to transforming the way people learn and grow in the digital era. Our mission is to deliver industry-relevant, high-quality online courses that empower learners to build real-world skills and thrive in today's fast-evolving tech landscape.
                  {isExpanded && (
                    <>
                      <br /><br />
                      Through expert-led instruction, hands-on projects, and flexible learning paths, we bridge the gap between knowledge and application. Whether you're starting your tech journey or advancing your career, Lotus Arc Technologies provides the tools, guidance, and inspiration you need to unlock your full potential.
                      <br /><br />
                      Join us as we cultivate innovation, nurture talent, and shape the future of technology—one skill, one course, and one success story at a time.
                    </>
                  )}
                </p>
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="lh-home__hero-read-more mt-2 text-primary-500 hover:text-primary-600 font-medium text-sm transition-colors"
                >
                  {isExpanded ? 'Read Less' : 'Read More'}
                </button>
              </div>
              <div className="lh-home__hero-cta flex flex-col sm:flex-row gap-4">
                <Link
                  to="/signup"
                  className="lh-home__hero-cta-primary btn-primary inline-flex items-center justify-center gap-2"
                >
                  <span className="lh-home__hero-cta-text">Get Started</span>
                  <svg className="lh-home__hero-cta-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
                <Link
                  to="/courses"
                  className="lh-home__hero-cta-secondary btn-outline inline-flex items-center justify-center gap-2"
                >
                  <span className="lh-home__hero-cta-text">Browse Courses</span>
                </Link>
              </div>
            </div>

            {/* Hero Image */}
            <div className="lh-home__hero-image relative animate-float hidden lg:block">
              <div className="lh-home__hero-image-wrapper relative">
                <img
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600"
                  alt="Students learning"
                  className="lh-home__hero-img rounded-2xl shadow-2xl w-full"
                />
                {/* Floating cards */}
                <div className="lh-home__hero-floating-card lh-home__hero-floating-card--complete absolute -left-8 top-1/4 bg-white p-4 rounded-xl shadow-lg animate-pulse">
                  <div className="lh-home__hero-floating-content flex items-center gap-3">
                    <div className="lh-home__hero-floating-icon w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="lh-home__hero-floating-text">
                      <p className="lh-home__hero-floating-title text-sm font-semibold text-dark-900">Course Completed!</p>
                      <p className="lh-home__hero-floating-subtitle text-xs text-dark-500">Java Full Stack</p>
                    </div>
                  </div>
                </div>
                <div className="lh-home__hero-floating-card lh-home__hero-floating-card--certificate absolute -right-8 bottom-1/4 bg-white p-4 rounded-xl shadow-lg">
                  <div className="lh-home__hero-floating-content flex items-center gap-3">
                    <div className="lh-home__hero-floating-icon w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="lh-home__hero-floating-text">
                      <p className="lh-home__hero-floating-title text-sm font-semibold text-dark-900">Certificate Earned</p>
                      <p className="lh-home__hero-floating-subtitle text-xs text-dark-500">DevOps</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Preview Section */}
      <section className="lh-home__courses py-16 md:py-24 bg-white">
        <div className="lh-home__courses-container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lh-home__courses-header text-center mb-12">
            <h2 className="lh-home__courses-title section-title text-3xl md:text-4xl font-bold text-dark-900 mb-4">
              Explore Our Popular Courses
            </h2>
            <p className="lh-home__courses-subtitle section-subtitle text-lg text-dark-500 max-w-2xl mx-auto">
              From beginner to advanced, find the perfect course to advance your career
            </p>
          </div>
          <div className="lh-home__courses-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {loadingCourses ? (
              [...Array(4)].map((_, i) => (
                <CourseCardSkeleton key={i} />
              ))
            ) : featuredCourses.length > 0 ? (
              featuredCourses.map((course) => (
              <CourseCard key={course.id} course={course} className="lh-home__courses-card" />
              ))
            ) : (
              <div className="col-span-4 text-center py-12 text-dark-500">
                No courses available at the moment
              </div>
            )}
          </div>
          <div className="lh-home__courses-cta text-center mt-12">
            <Link
              to="/courses"
              className="lh-home__courses-cta-btn btn-outline inline-flex items-center gap-2"
            >
              <span className="lh-home__courses-cta-text">View All Courses</span>
              <svg className="lh-home__courses-cta-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="lh-home__how py-16 md:py-24 bg-dark-50">
        <div className="lh-home__how-container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lh-home__how-header text-center mb-12">
            <h2 className="lh-home__how-title section-title text-3xl md:text-4xl font-bold text-dark-900 mb-4">
              How It Works
            </h2>
            <p className="lh-home__how-subtitle section-subtitle text-lg text-dark-500 max-w-2xl mx-auto">
              Get started in four simple steps
            </p>
          </div>
          <div className="lh-home__how-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {howItWorks.map((item) => (
              <div key={item.step} className={`lh-home__how-card lh-home__how-card--step-${item.step} text-center p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow`}>
                <div className="lh-home__how-step flex items-center justify-center w-16 h-16 bg-primary-100 text-primary-500 rounded-full mx-auto mb-6">
                  {item.icon}
                </div>
                <span className="lh-home__how-number text-sm font-bold text-primary-500 mb-2 block">
                  Step {item.step}
                </span>
                <h3 className="lh-home__how-card-title text-xl font-bold text-dark-900 mb-3">
                  {item.title}
                </h3>
                <p className="lh-home__how-card-description text-dark-500">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="lh-home__why py-16 md:py-24 bg-white">
        <div className="lh-home__why-container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lh-home__why-header text-center mb-12">
            <h2 className="lh-home__why-title section-title text-3xl md:text-4xl font-bold text-dark-900 mb-4">
              Why Choose LAT?
            </h2>
            <p className="lh-home__why-subtitle section-subtitle text-lg text-dark-500 max-w-2xl mx-auto">
              We're committed to delivering the best learning experience
            </p>
          </div>
          <div className="lh-home__why-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className={`lh-home__why-card lh-home__why-card--${index + 1} text-center p-6 rounded-2xl hover:bg-primary-50 transition-colors group`}>
                <div className="lh-home__why-icon-wrapper text-dark-300 group-hover:text-primary-500 transition-colors mb-4 flex justify-center">
                  {item.icon}
                </div>
                <h3 className="lh-home__why-card-title text-lg font-bold text-dark-900 mb-2">
                  {item.title}
                </h3>
                <p className="lh-home__why-card-description text-dark-500 text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="lh-home__cta py-16 md:py-24 bg-gradient-to-r from-primary-500 to-primary-600">
        <div className="lh-home__cta-container max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="lh-home__cta-title text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your Learning Journey?
          </h2>
          <p className="lh-home__cta-subtitle text-lg text-primary-100 mb-8 max-w-2xl mx-auto">
            Join thousands of learners who have transformed their careers with LAT
          </p>
          <div className="lh-home__cta-buttons flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className="lh-home__cta-btn lh-home__cta-btn--primary bg-white text-primary-600 hover:bg-primary-50 font-semibold py-3 px-8 rounded-lg transition-colors shadow-lg"
            >
              Start Learning
            </Link>
            <Link
              to="/courses"
              className="lh-home__cta-btn lh-home__cta-btn--secondary border-2 border-white text-white hover:bg-white hover:text-primary-600 font-semibold py-3 px-8 rounded-lg transition-colors"
            >
              Explore Courses
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
